/**
 * M0 template gallery + measurement plan — pure module (Phase 5, plan of record
 * modules/pricing-experiments/plans/2026-08-31-pricing-loop-plan-v2.md §12 prod
 * resume point §4; acceptance AC-030/031 + refinements).
 *
 * Ranks ≥ 4 menu templates on PRIORS ONLY (benchmark book v1 —
 * `benchmarkBook.ts` mirrors `modules/product-knowledge/knowledge/benchmark-book-v1.md`)
 * through the deterministic kernel `runFrontier`: every template is a
 * `scenario` menu, the classic menu is the baseline policy, and the estimand
 * is value per ELIGIBLE PAYWALL VISITOR at a 12-month horizon. The seed is a
 * hash of the three inputs, so equal inputs reproduce the order, the bands
 * and the input hash byte for byte (AC-030.3).
 *
 * Pure: no React, no Firebase, no network, no warehouse reads.
 */

import {
  fnv1aHash,
  runFrontier,
  stableStringify,
  type FrontierInput,
  type MenuChoiceEvidence,
  type OfferSpec,
  type OfferValueEvidence,
} from '@modules/pricing-offer-emulator';
import {
  deriveLadderFromAnchor,
  menuTakes,
  sumTakes,
  round99,
  type CalculatorOffer,
  type ChainState,
} from './pricingCalculator';
import {
  BENCHMARK_BOOK_VERSION,
  BOOK_DEFAULT_TRAFFIC_PER_DAY,
  BOOK_MONTHLY_RENEWAL,
  BOOK_PRIOR_STRENGTH,
  BOOK_SOLO_PRIORS,
  BOOK_THRESHOLDS,
  BOOK_TRIAL_DAYS,
  BOOK_TRIAL_MATURITY_SLACK_DAYS,
  BOOK_TRIAL_TO_PAID,
  BOOK_WEEKLY_RENEWAL,
  CATEGORY_MODIFIERS,
  CHAIN_PRIOR_BANDS,
  type GalleryCategory,
  type PriorBand,
} from './benchmarkBook';

export type { GalleryCategory } from './benchmarkBook';

// ---------------------------------------------------------------------------
// Inputs and templates
// ---------------------------------------------------------------------------

export interface GalleryInputs {
  category: GalleryCategory;
  /** Monthly anchor price, $. Non-positive → the book default (BB-17). */
  anchorMonthly: number;
  /** Expected starts per day. Non-positive → plan rows typed `unavailable`. */
  trafficPerDay: number;
}

export const DEFAULT_GALLERY_INPUTS: GalleryInputs = {
  category: 'other',
  anchorMonthly: deriveLadderFromAnchor(0).offers['ladder-monthly'].price as number,
  trafficPerDay: BOOK_DEFAULT_TRAFFIC_PER_DAY,
};

export type GalleryTemplateId = 'annual_trial' | 'two_tiers' | 'classic' | 'fast_cash';

/** Ladder ids (calculator AC-003) + the gallery's trial variant of the annual. */
export type LadderOfferId =
  | 'ladder-monthly'
  | 'ladder-annual'
  | 'ladder-weekly'
  | 'ladder-premium'
  | 'ladder-annual-trial';

export const TRIAL_OFFER_ID: LadderOfferId = 'ladder-annual-trial';

export interface GalleryTemplateSpec {
  id: GalleryTemplateId;
  label: string;
  menu: LadderOfferId[];
  /** When the cash arrives — the goal axis the user chooses by. */
  cashTiming: string;
  pickWhen: string;
}

/** The classic two-offer menu is the reference every other template is compared to. */
export const GALLERY_REFERENCE_TEMPLATE: GalleryTemplateId = 'classic';

export const GALLERY_TEMPLATES: GalleryTemplateSpec[] = [
  {
    id: 'annual_trial',
    label: 'Annual with trial',
    menu: ['ladder-monthly', 'ladder-annual-trial'],
    cashTiming: 'cash arrives after the trial',
    pickWhen: 'long-term value over instant cash; your first experiment is built in (trial vs no trial).',
  },
  {
    id: 'two_tiers',
    label: 'Two tiers',
    menu: ['ladder-monthly', 'ladder-premium', 'ladder-annual'],
    cashTiming: 'cash on signup',
    pickWhen: 'a premium tier has real extra value to sell; the ladder anchors the middle price.',
  },
  {
    id: 'classic',
    label: 'Classic',
    menu: ['ladder-monthly', 'ladder-annual'],
    cashTiming: 'maximum instant cash',
    pickWhen: 'simplest to run; the reference every other template is measured against.',
  },
  {
    id: 'fast_cash',
    label: 'Fast cash',
    menu: ['ladder-weekly', 'ladder-monthly', 'ladder-annual'],
    cashTiming: 'weekly decays fast — entry price, not a base',
    pickWhen: 'you need first payments this week and can live with churn; weekly is the entry, not the base.',
  },
];

// ---------------------------------------------------------------------------
// Ladder + trial offer (BB-17, BB-08)
// ---------------------------------------------------------------------------

export interface GalleryLadder {
  offers: Record<string, CalculatorOffer>;
  anchor: number;
}

/** The calculator's anchor ladder plus the annual-with-trial variant. */
export function galleryLadder(anchorMonthly: number): GalleryLadder {
  const ladder = deriveLadderFromAnchor(anchorMonthly);
  const anchor = ladder.offers['ladder-monthly'].price as number;
  const annualPrice = ladder.offers['ladder-annual'].price as number;
  const trial: CalculatorOffer = {
    id: TRIAL_OFFER_ID,
    label: `Annual $${annualPrice.toFixed(2)} · ${BOOK_TRIAL_DAYS}-day free trial`,
    family: 'annual',
    price: annualPrice,
    priceState: 'scenario',
    trialDays: BOOK_TRIAL_DAYS,
    isSubscription: true,
  };
  return { offers: { ...ladder.offers, [TRIAL_OFFER_ID]: trial }, anchor };
}

/** Solo anchors per ladder offer under the category modifier (BB-05..08, BB-C2). */
export function gallerySolos(category: GalleryCategory): Record<LadderOfferId, number> {
  const modifier = CATEGORY_MODIFIERS[category];
  const weekly = BOOK_SOLO_PRIORS.weekly * (modifier.weeklySoloMultiplier ?? 1);
  return {
    'ladder-monthly': BOOK_SOLO_PRIORS.monthly,
    'ladder-annual': BOOK_SOLO_PRIORS.annual,
    'ladder-weekly': weekly,
    'ladder-premium': BOOK_SOLO_PRIORS.premium,
    'ladder-annual-trial': BOOK_SOLO_PRIORS.trial,
  };
}

export function galleryTrialToPaid(category: GalleryCategory): number {
  return CATEGORY_MODIFIERS[category].trialToPaid ?? BOOK_TRIAL_TO_PAID;
}

// ---------------------------------------------------------------------------
// Kernel bundle from priors
// ---------------------------------------------------------------------------

const HORIZON_MONTHS = 12;
const DRAWS = 2000;

const INTERVAL_BY_FAMILY: Record<CalculatorOffer['family'], OfferSpec['interval']> = {
  monthly: 'month',
  annual: 'year',
  weekly: 'week',
  daily: 'week', // not produced by the ladder
  one_time: undefined,
};

function offerSpec(offer: CalculatorOffer): OfferSpec {
  return {
    offerId: offer.id,
    productId: offer.id,
    interval: INTERVAL_BY_FAMILY[offer.family],
    intervalCount: 1,
    nominalMinor: Math.round((offer.price ?? 0) * 100),
    currency: 'usd',
    trialDays: offer.trialDays ?? 0,
    claim: 'scenario',
  };
}

function valueEvidence(offer: CalculatorOffer, category: GalleryCategory): OfferValueEvidence {
  const price = offer.price ?? 0;
  const n = BOOK_PRIOR_STRENGTH.subscribersPerRenewalGate;
  if ((offer.trialDays ?? 0) > 0) {
    const starters = BOOK_PRIOR_STRENGTH.trialStarters;
    return {
      kind: 'trial',
      trialToPaidNumerator: Math.round(galleryTrialToPaid(category) * starters),
      trialToPaidDenominator: starters,
      underlyingOfferId: 'ladder-annual',
    };
  }
  switch (offer.family) {
    case 'annual':
      return { kind: 'upfront', upfrontValue: price };
    case 'weekly':
      return {
        kind: 'weekly_renewal',
        r1Numerator: Math.round(BOOK_WEEKLY_RENEWAL.r1 * n),
        r1Denominator: n,
        r2Numerator: Math.round(BOOK_WEEKLY_RENEWAL.r2 * n),
        r2Denominator: n,
        firstPaymentArpu: price,
        renewalArpu: price,
      };
    default:
      return {
        kind: 'renewal_census',
        r1Numerator: Math.round(BOOK_MONTHLY_RENEWAL.r1 * n),
        r1Denominator: n,
        r2Numerator: Math.round(BOOK_MONTHLY_RENEWAL.r2 * n),
        r2Denominator: n,
        firstPaymentArpu: price,
        renewalArpu: price,
      };
  }
}

/**
 * Seed = hash of the EFFECTIVE priors the inputs resolve to (+ book version):
 * equal inputs ⇒ equal draws, and a category without a sourced modifier
 * (education, lifestyle) reproduces the global gallery byte for byte — the
 * category label itself never changes the numbers (AC-030.3, AC-031.3).
 */
export function gallerySeed(inputs: GalleryInputs): number {
  const modifier = CATEGORY_MODIFIERS[inputs.category];
  const normalized = {
    book: BENCHMARK_BOOK_VERSION,
    anchor: round99(inputs.anchorMonthly > 0 ? inputs.anchorMonthly : DEFAULT_GALLERY_INPUTS.anchorMonthly),
    traffic: inputs.trafficPerDay,
    trialToPaid: modifier.trialToPaid ?? BOOK_TRIAL_TO_PAID,
    weeklySoloMultiplier: modifier.weeklySoloMultiplier ?? 1,
  };
  return Number.parseInt(fnv1aHash(stableStringify(normalized)), 16);
}

export function buildGalleryFrontierInput(inputs: GalleryInputs): FrontierInput {
  const { offers } = galleryLadder(inputs.anchorMonthly);
  const solos = gallerySolos(inputs.category);
  const p3 = CHAIN_PRIOR_BANDS.p3.p50;
  const users = BOOK_PRIOR_STRENGTH.paywallVisitorsPerMenu;
  const usedIds = new Set<string>();
  GALLERY_TEMPLATES.forEach(template => template.menu.forEach(id => usedIds.add(id)));
  // The trial's underlying offer must exist even when no template lists it alone.
  usedIds.add('ladder-annual');
  const usedOffers = [...usedIds].map(id => offers[id]);

  const choiceEvidence: MenuChoiceEvidence[] = GALLERY_TEMPLATES.map(template => {
    const takes = menuTakes(template.menu, offers, solos);
    const choices: Record<string, number> = {};
    template.menu.forEach(id => {
      choices[id] = Math.round(users * p3 * (takes[id] ?? 0));
    });
    return { menuId: template.id, users, choices };
  });

  const evidence: Record<string, OfferValueEvidence> = {};
  usedOffers.forEach(offer => {
    evidence[offer.id] = valueEvidence(offer, inputs.category);
  });

  return {
    seed: gallerySeed(inputs),
    draws: DRAWS,
    horizonMonths: HORIZON_MONTHS,
    offers: usedOffers.map(offerSpec),
    menus: GALLERY_TEMPLATES.map(template => ({
      menuId: template.id,
      offerIds: [...template.menu],
      defaultOfferId: template.menu[0],
      claim: 'scenario' as const,
    })),
    choiceEvidence,
    valueEvidence: evidence,
    exposure: { paywallReached: users, exposureCovered: users, exposureMissing: 0 },
    baseline: { policyId: GALLERY_REFERENCE_TEMPLATE, kind: 'global_menu', menuId: GALLERY_REFERENCE_TEMPLATE },
    candidatePolicies: GALLERY_TEMPLATES.filter(template => template.id !== GALLERY_REFERENCE_TEMPLATE).map(template => ({
      policyId: template.id,
      kind: 'global_menu' as const,
      menuId: template.id,
    })),
    valueBasis: 'gross_audit',
  };
}

// ---------------------------------------------------------------------------
// Ranked rows
// ---------------------------------------------------------------------------

export interface GalleryRow {
  id: GalleryTemplateId;
  label: string;
  /** «Monthly $19.99 + Annual $119.99 · 7-day free trial» */
  menuLine: string;
  menu: LadderOfferId[];
  /** $ per eligible paywall visitor, 12-month horizon (kernel P10/P50/P90). */
  p10: number;
  p50: number;
  p90: number;
  /** Fraction of joint draws where the template beats the classic; null for the classic. */
  probBetterThanReference: number | null;
  isReference: boolean;
  cashTiming: string;
  pickWhen: string;
  /** Σ takes of pressed users (prior, nested logit) — the plan's conversions rate. */
  takeSum: number;
  /** Take of the trial offer when the template has one (plan's trial starts). */
  trialTake: number | null;
  hasWeekly: boolean;
  hasTrial: boolean;
  /** Benchmark-book entry ids this card stands on (AC-031.3). */
  priorEntries: string[];
}

export interface GalleryOverlap {
  /** Leader + every template whose P90 reaches the leader's P10. */
  count: number;
  line: string;
}

export interface GalleryResult {
  inputs: GalleryInputs;
  seed: number;
  inputHash: string;
  bookVersion: string;
  ladder: GalleryLadder;
  rows: GalleryRow[];
  overlap: GalleryOverlap | null;
  categoryNote: string;
}

function menuLine(menu: LadderOfferId[], offers: Record<string, CalculatorOffer>): string {
  const parts = menu.map(id => {
    const offer = offers[id];
    if ((offer.trialDays ?? 0) > 0) return `Annual $${(offer.price ?? 0).toFixed(2)}`;
    return offer.label;
  });
  const trial = menu.map(id => offers[id]).find(offer => (offer.trialDays ?? 0) > 0);
  const suffix = trial ? ` · ${trial.trialDays}-day free trial` : ' · no trial';
  return `${parts.join(' + ')}${suffix}`;
}

function priorEntriesFor(menu: LadderOfferId[], category: GalleryCategory): string[] {
  const entries = new Set<string>(['BB-04', 'BB-09', 'BB-16']);
  menu.forEach(id => {
    switch (id) {
      case 'ladder-monthly':
        entries.add('BB-05');
        entries.add('BB-12');
        entries.add('BB-13');
        break;
      case 'ladder-premium':
        entries.add('BB-24');
        entries.add('BB-12');
        entries.add('BB-13');
        break;
      case 'ladder-annual':
        entries.add('BB-06');
        entries.add('BB-15');
        break;
      case 'ladder-weekly':
        entries.add('BB-07');
        entries.add('BB-14');
        break;
      case 'ladder-annual-trial':
        entries.add('BB-08');
        entries.add('BB-10');
        entries.add('BB-15');
        break;
      default:
        break;
    }
  });
  entries.add(CATEGORY_MODIFIERS[category].entryId);
  return [...entries].sort();
}

/**
 * Rank the templates on priors. Deterministic — same inputs ⇒ same rows,
 * same `inputHash` (AC-030.3). Sort: P50 desc, then template id (stable).
 */
export function computeGallery(inputs: GalleryInputs): GalleryResult {
  const frontierInput = buildGalleryFrontierInput(inputs);
  const result = runFrontier(frontierInput);
  const ladder = galleryLadder(inputs.anchorMonthly);
  const solos = gallerySolos(inputs.category);
  const byPolicy = new Map(result.frontier.map(row => [row.policyId, row]));
  const rows: GalleryRow[] = GALLERY_TEMPLATES.map(template => {
    const frontier = byPolicy.get(template.id);
    if (!frontier) throw new Error(`gallery template ${template.id} missing from the frontier`);
    const takes = menuTakes(template.menu, ladder.offers, solos);
    const trialId = template.menu.find(id => (ladder.offers[id].trialDays ?? 0) > 0);
    return {
      id: template.id,
      label: template.label,
      menuLine: menuLine(template.menu, ladder.offers),
      menu: [...template.menu],
      p10: frontier.p10,
      p50: frontier.p50,
      p90: frontier.p90,
      probBetterThanReference: frontier.probBetterThanBaseline,
      isReference: template.id === GALLERY_REFERENCE_TEMPLATE,
      cashTiming: template.cashTiming,
      pickWhen: template.pickWhen,
      takeSum: sumTakes(takes),
      trialTake: trialId ? takes[trialId] ?? 0 : null,
      hasWeekly: template.menu.includes('ladder-weekly'),
      hasTrial: Boolean(trialId),
      priorEntries: priorEntriesFor(template.menu, inputs.category),
    };
  });
  rows.sort((a, b) => (b.p50 - a.p50) || a.id.localeCompare(b.id));
  const leader = rows[0];
  const overlapping = rows.filter((row, index) => index > 0 && row.p90 >= leader.p10);
  const overlap: GalleryOverlap | null = overlapping.length > 0
    ? {
      count: overlapping.length + 1,
      line: `Priors do not separate the top ${overlapping.length + 1} — the bands overlap. Choose by goal; the measurement plan below is what turns priors into your numbers.`,
    }
    : null;
  return {
    inputs,
    seed: frontierInput.seed,
    inputHash: result.inputHash,
    bookVersion: BENCHMARK_BOOK_VERSION,
    ladder,
    rows,
    overlap,
    categoryNote: CATEGORY_MODIFIERS[inputs.category].note,
  };
}

// ---------------------------------------------------------------------------
// Measurement plan (AC-031)
// ---------------------------------------------------------------------------

export type PlanRowKey = 'unlock' | 'solid_choice' | 'trial_verdict' | 'weekly_renewal' | 'annual_renewal';

export type PlanRow =
  | {
      key: PlanRowKey;
      decision: string;
      accumulate: string;
      kind: 'days';
      /** Displayed P50 day (⌈d⌉ + lag). */
      day: number;
      /** [earliest (P90 chain), latest (P10 chain)] displayed days. */
      band: [number, number];
      /** Raw accumulation days per chain band — 10× at 1/10 traffic (AC-031.2). */
      rawDays: PriorBand;
    }
  | { key: PlanRowKey; decision: string; accumulate: string; kind: 'month'; month: number }
  | { key: PlanRowKey; decision: string; accumulate: string; kind: 'unavailable'; reason: string };

interface ChainRates {
  paywallPerDay: number;
  pressedPerDay: number;
}

function chainAt(traffic: number, pick: (band: PriorBand) => number): ChainRates {
  const chain: ChainState = {
    cps: pick(CHAIN_PRIOR_BANDS.cps),
    p1: pick(CHAIN_PRIOR_BANDS.p1),
    p2: pick(CHAIN_PRIOR_BANDS.p2),
    p3: pick(CHAIN_PRIOR_BANDS.p3),
  };
  const paywallPerDay = traffic * chain.p1 * chain.p2;
  return { paywallPerDay, pressedPerDay: paywallPerDay * chain.p3 };
}

const displayDay = (rawDays: number): number => Math.ceil(rawDays) + BOOK_THRESHOLDS.warehouseLagDays;

function daysRow(
  key: PlanRowKey,
  decision: string,
  accumulate: string,
  traffic: number,
  unitsPerDay: (rates: ChainRates) => number,
  units: number,
  extraDays = 0,
): PlanRow {
  const raw = (pick: (band: PriorBand) => number): number => units / unitsPerDay(chainAt(traffic, pick)) + extraDays;
  const rawDays: PriorBand = { p10: raw(b => b.p10), p50: raw(b => b.p50), p90: raw(b => b.p90) };
  return {
    key,
    decision,
    accumulate,
    kind: 'days',
    day: displayDay(rawDays.p50),
    // P90 chain = fastest accumulation = the earliest date.
    band: [displayDay(rawDays.p90), displayDay(rawDays.p10)],
    rawDays,
  };
}

/**
 * Calendar of decision dates at the expected traffic for ONE template
 * (AC-031.1 rule): «day ⌈d⌉ + 1» with d = units ÷ units-per-day across the
 * chain prior bands; the annual row never scales with traffic.
 */
export function measurementPlan(inputs: GalleryInputs, row: GalleryRow): PlanRow[] {
  const traffic = inputs.trafficPerDay;
  const { unlockPaywallVisitors, unlockConversions, solidPressed, trialVerdictStarts, weeklyFirstRenewalDay, annualRenewalMonth, warehouseLagDays } = BOOK_THRESHOLDS;
  const rows: PlanRow[] = [];
  const unavailable = (key: PlanRowKey, decision: string, accumulate: string): PlanRow => ({
    key, decision, accumulate, kind: 'unavailable', reason: 'enter expected traffic (starts / day) to date this row',
  });
  const trafficOk = Number.isFinite(traffic) && traffic > 0;
  const unlockLabel = `${unlockPaywallVisitors} paywall users or ${unlockConversions} conversions`;
  if (trafficOk) {
    // OR-threshold: the earlier of the two accumulations, per chain band.
    const raw = (pick: (band: PriorBand) => number): number => {
      const rates = chainAt(traffic, pick);
      return Math.min(
        unlockPaywallVisitors / rates.paywallPerDay,
        unlockConversions / (rates.pressedPerDay * row.takeSum),
      );
    };
    const rawDays: PriorBand = { p10: raw(b => b.p10), p50: raw(b => b.p50), p90: raw(b => b.p90) };
    rows.push({
      key: 'unlock',
      decision: 'Planner unlocks',
      accumulate: unlockLabel,
      kind: 'days',
      day: displayDay(rawDays.p50),
      band: [displayDay(rawDays.p90), displayDay(rawDays.p10)],
      rawDays,
    });
    rows.push(daysRow('solid_choice', 'Choice rates become solid', `~${solidPressed} pressed`, traffic, rates => rates.pressedPerDay, solidPressed));
    if (row.hasTrial && row.trialTake && row.trialTake > 0) {
      const maturity = BOOK_TRIAL_DAYS + BOOK_TRIAL_MATURITY_SLACK_DAYS;
      rows.push(daysRow(
        'trial_verdict',
        'Trial verdict',
        `~${trialVerdictStarts} trial starts + ${BOOK_TRIAL_DAYS}-day trial + ${BOOK_TRIAL_MATURITY_SLACK_DAYS} maturity days`,
        traffic,
        rates => rates.pressedPerDay * (row.trialTake as number),
        trialVerdictStarts,
        maturity,
      ));
    }
  } else {
    rows.push(unavailable('unlock', 'Planner unlocks', unlockLabel));
    rows.push(unavailable('solid_choice', 'Choice rates become solid', `~${solidPressed} pressed`));
    if (row.hasTrial) rows.push(unavailable('trial_verdict', 'Trial verdict', `~${trialVerdictStarts} trial starts + maturity`));
  }
  if (row.hasWeekly) {
    // First weekly renewal is due on day 7 whatever the traffic; +lag to read it.
    const day = weeklyFirstRenewalDay + warehouseLagDays;
    rows.push({
      key: 'weekly_renewal',
      decision: 'Weekly renewals observable',
      accumulate: `first cycle renews on day ${weeklyFirstRenewalDay}`,
      kind: 'days',
      day,
      band: [day, day],
      rawDays: { p10: weeklyFirstRenewalDay, p50: weeklyFirstRenewalDay, p90: weeklyFirstRenewalDay },
    });
  }
  rows.push({
    key: 'annual_renewal',
    decision: 'Annual renewals observable',
    accumulate: 'cohort age ≥ 12 months',
    kind: 'month',
    month: annualRenewalMonth,
  });
  return rows;
}

// ---------------------------------------------------------------------------
// Calculator handoff (AC-030.5)
// ---------------------------------------------------------------------------

export interface GalleryTemplatePrefill {
  templateId: GalleryTemplateId;
  label: string;
  anchorMonthly: number;
  trafficPerDay: number;
  menu: LadderOfferId[];
  /** The annual-with-trial offer to merge into the calculator palette when the template uses it. */
  trialOffer: CalculatorOffer | null;
}

export function galleryTemplatePrefill(result: GalleryResult, row: GalleryRow): GalleryTemplatePrefill {
  return {
    templateId: row.id,
    label: row.label,
    anchorMonthly: result.ladder.anchor,
    trafficPerDay: result.inputs.trafficPerDay > 0 ? result.inputs.trafficPerDay : BOOK_DEFAULT_TRAFFIC_PER_DAY,
    menu: [...row.menu],
    trialOffer: row.hasTrial ? result.ladder.offers[TRIAL_OFFER_ID] : null,
  };
}

/** Whole-number percent for the «beats the classic» line. */
export const pctWhole = (value: number): string => `${Math.round(value * 100)}%`;
